import { linko } from "@shared/linko";

export type BackgroundImageItem = {
  imageId: string;
  url: string;
  subCategoryIds: string[];
  populationIds: string[];
  opportunityCount: number;
  isOrganizationDefault: boolean;
  isUnused: boolean;
};

export type ImageUsageResult = {
  imageId: string;
  organizationId: string | null;
  isBackground: boolean;
  opportunities: Array<{ id: string; title: string }>;
  isOrganizationDefault: boolean;
  isOrganizationImage: boolean;
  isProtectedSystemImage: boolean;
  requiresReplacement: boolean;
  canDelete: boolean;
};

type ImageQueriesApi = {
  getOrganizationBackgroundImagesUrls: (
    organizationId: string,
  ) => Promise<{ data: BackgroundImageItem[] }>;
  getImageUsage: (imageId: string) => Promise<{ data: ImageUsageResult }>;
};

type ImageMutationsApi = {
  deleteOrganizationBackgroundImage: (body: {
    imageId: string;
    replacementImageId?: string;
  }) => Promise<{ data: { success: true } }>;
  setBackgroundImageAssignments: (body: {
    imageId: string;
    subCategoryIds: string[];
    populationIds: string[];
  }) => Promise<{ data: { success: true } }>;
  deleteUnusedOrganizationBackgroundImages: (body: {
    organizationId: string;
  }) => Promise<{ data: { deletedCount: number } }>;
};

const imageQueries = () =>
  linko.image.queries as unknown as ImageQueriesApi;

const imageMutations = () =>
  linko.image.mutations as unknown as ImageMutationsApi;

export const organizationBackgroundImagesQueryKey = (
  organizationId?: string,
) => ["organization-background-images", organizationId] as const;

export async function fetchOrganizationBackgroundImages(
  organizationId: string,
): Promise<BackgroundImageItem[]> {
  const { data } =
    await imageQueries().getOrganizationBackgroundImagesUrls(organizationId);
  return data;
}

export async function fetchImageUsage(
  imageId: string,
): Promise<ImageUsageResult> {
  const { data } = await imageQueries().getImageUsage(imageId);
  return data;
}

export async function deleteOrganizationBackgroundImage(body: {
  imageId: string;
  replacementImageId?: string;
}): Promise<void> {
  await imageMutations().deleteOrganizationBackgroundImage(body);
}

export async function setBackgroundImageAssignments(body: {
  imageId: string;
  subCategoryIds: string[];
  populationIds: string[];
}): Promise<void> {
  await imageMutations().setBackgroundImageAssignments(body);
}

export async function deleteUnusedOrganizationBackgroundImages(
  organizationId: string,
): Promise<number> {
  const { data } =
    await imageMutations().deleteUnusedOrganizationBackgroundImages({
      organizationId,
    });
  return data.deletedCount;
}
